import { useEffect, useMemo, useState } from 'react';
import { CALL_QUALITY_RATE, describeTrack, measureSampleRate, muteState, type CapturedSource } from '../lib/sources.ts';
import { QualityCard } from './QualityCard.tsx';
import { saveFolder } from '../lib/save-folder.ts';
import { account, isEmail } from '../lib/account.ts';
import { api } from '../lib/api.ts';
import { StudioEngine } from '../lib/studio.ts';
import { unlockAudio } from '../lib/track-processor.ts';
import { AccountMenu, AppBar, Icon, initials, Logo, Menu, Meter, roleLabel, useNow, useObservable, useTitle, VideoEl } from './common.tsx';
import { ReplayModal } from './ReplayModal.tsx';
import { RoomsPanel } from './RoomsPanel.tsx';
import { Stage } from './Stage.tsx';
import { SignInCode } from './SignInCode.tsx';
import { SourcesPanel } from './SourcesPanel.tsx';
import { TeamPanel } from './TeamPanel.tsx';
import { UploadsPanel } from './UploadsPanel.tsx';
import { feedbackUi } from './Feedback.tsx';

export function StudioPage({ studioId, studioKey }: { studioId: string; studioKey: string }) {
  const engine = useMemo(() => new StudioEngine(studioId, studioKey), [studioId, studioKey]);
  useObservable(engine);
  useObservable(engine.sources);
  useEffect(() => {
    (window as any).takehouse = engine; // handy for debugging and the e2e test
    void engine.init();
    return () => void engine.leave();
  }, [engine]);
  // Feedback sent from here can say who this person is in the studio and what was going on.
  useEffect(
    () =>
      feedbackUi.describeStudio(() => ({
        studioId: engine.studioId,
        participantId: engine.participantId,
        role: engine.role,
        name: engine.name,
        connected: engine.connected,
        peers: engine.peers.size,
        takeId: engine.recording?.takeId ?? engine.lastTake?.takeId,
        recording: !!engine.recording,
        sources: engine.sources.active.map((s) => `${s.kind}: ${s.label}${s.use.record ? ', recorded' : ''}${s.use.live ? ', live' : ''}`),
      })),
    [engine],
  );
  useTitle(engine.recording ? `● REC · ${engine.studio?.name ?? ''}` : (engine.studio?.name ?? ''));

  if (engine.fatal && !engine.joined) {
    return (
      <div className="page">
        <AppBar />
        <main className="center-page">
          <div className="card">
            <span className="ready-icon" style={{ background: 'var(--bad-soft)', color: 'var(--bad)' }}>
              <Icon name="warn" />
            </span>
            <h2>Can’t open this studio</h2>
            <p className="muted">{engine.fatal}</p>
            <a className="btn" href="/" style={{ alignSelf: 'flex-start' }}>
              <Icon name="arrowLeft" size={16} /> Go to the home page
            </a>
          </div>
        </main>
      </div>
    );
  }
  return (
    <>
      {engine.joined ? engine.waiting || (engine.fatal && !engine.room) ? <Lobby engine={engine} /> : <Room engine={engine} /> : <GreenRoom engine={engine} />}
      {engine.check && <ReplayModal engine={engine} run={engine.check} />}
    </>
  );
}

// ---------------------------------------------------------------------------

function GreenRoom({ engine }: { engine: StudioEngine }) {
  const sm = engine.sources;
  useObservable(account);
  const me = account.account;
  const [name, setName] = useState(localStorage.getItem('takehouse.name') || me?.name || '');
  const [email, setEmail] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  /** An emailed sign-in code is awaited for this address. */
  const [pending, setPending] = useState<string | null>(null);
  const cam = sm.primaryCamera ?? sm.active.find((s) => s.kind === 'camera');
  const mic = sm.primaryMic ?? sm.active.find((s) => s.kind === 'microphone');
  const hw = engine.hardware;
  const micRate = useSampleRate(mic?.track);
  const role = engine.role;
  // Hosts and producers keep their studios and recordings under an email; for guests it's optional.
  const staffRole = role === 'host' || role === 'producer';
  const emailOk = !!me || (email.trim() ? isEmail(email) : !staffRole);
  const canJoin = !!name.trim() && !!hw && emailOk && !joining && !pending;

  const join = async () => {
    if (!canJoin) return;
    unlockAudio(); // first, while still inside the click: Safari only starts audio from a user gesture
    setJoining(true);
    setJoinError('');
    if (!me && email.trim()) {
      try {
        const r = await account.signIn(email, name);
        if ('pending' in r) {
          setPending(r.email);
          setJoining(false);
          return;
        }
        signedIn();
      } catch (e) {
        setJoinError((e as Error).message);
        setJoining(false);
        return;
      }
    }
    engine.join(name);
  };
  // Puts this studio in their list (and makes them its owner if it has none yet).
  const signedIn = () => void api.studio(engine.studioId, engine.key).catch(() => {});

  return (
    <div className="page">
      <AppBar>
        <span className="crumb-sep" />
        <span className="crumb ellipsis">{engine.studio?.name ?? ''}</span>
        <span className="grow" />
        {role && <span className="pill">{roleLabel(role)}</span>}
      </AppBar>
      <main className="greenroom">
        <div className="greenroom-head">
          <div className="col" style={{ gap: 4 }}>
            <span className="eyebrow">{role === 'guest' ? 'You’re invited to' : role === 'host' ? 'You’re hosting' : role === 'producer' ? 'You’re producing' : 'Studio'}</span>
            <h1>{engine.studio?.name ?? '…'}</h1>
          </div>
        </div>

        <div className="col" style={{ gap: 16 }}>
          <div className="preview">
            {cam ? (
              <VideoEl track={cam.track} />
            ) : (
              <div className="placeholder">
                {hw ? <Icon name="camOff" size={32} /> : <span className="spinner" />}
                <span className="small">{hw ? 'No camera selected' : 'Starting camera…'}</span>
              </div>
            )}
            <div className="overlay">
              <span className="overlay-chip">
                <Icon name={mic ? 'mic' : 'micOff'} size={14} />
                {name.trim() || 'You'}
                {mic && <Meter track={mic.track} />}
              </span>
            </div>
          </div>
          {sm.error && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {sm.error}
            </div>
          )}
          {sm.soundNote && (
            <div className="alert info">
              <Icon name="speaker" size={16} />
              <span className="grow">{sm.soundNote}</span>
              <button className="ghost sm icon" onClick={() => sm.dismissSoundNote()} aria-label="Dismiss">
                <Icon name="close" size={13} />
              </button>
            </div>
          )}
          {engine.otherTab && (
            <div className="alert info">
              <Icon name="info" size={16} />
              <span>
                Wordstow is already open in another tab of this browser, so this tab joins as a separate person. Browsers let only
                one tab use the camera reliably: close the other tab unless you’re deliberately testing with two.
              </span>
            </div>
          )}
          <div className="card test-card">
            <span className="ficon">
              <Icon name="replay" size={17} />
            </span>
            <div className="col grow" style={{ gap: 2 }}>
              <h3>Test before you join</h3>
              <p className="muted small">
                Makes a short real recording, reads it back from disk and plays it to you, checking sound level, frame rate and
                picture automatically.
              </p>
            </div>
            <button onClick={() => { unlockAudio(); void engine.runCheck('preflight'); }} disabled={!hw}>
              <Icon name="replay" size={16} /> Record a 6-second test
            </button>
          </div>
        </div>

        <div className="col" style={{ gap: 16 }}>
          <div className="card join-card">
            <div className="col" style={{ gap: 2 }}>
              <h2>Ready to join?</h2>
              <span className="muted small">Check how you look and sound, then step into the studio.</span>
            </div>
            <label className="field">
              Your name
              <input value={name} autoFocus placeholder="How others will see you" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void join()} />
              {me && (
                <span className="field-hint">
                  Signed in as {me.email} ·{' '}
                  <button className="linklike" onClick={() => void account.signOut()}>
                    Not you?
                  </button>
                </span>
              )}
            </label>
            {!me && pending && (
              <div className="field">
                Email
                <SignInCode
                  email={pending}
                  name={name}
                  onDone={() => {
                    setPending(null);
                    signedIn();
                    engine.join(name);
                  }}
                  onCancel={() => {
                    setPending(null);
                    if (staffRole) return;
                    setEmail('');
                    engine.join(name);
                  }}
                  cancelLabel={staffRole ? 'Use a different email' : 'Join without email'}
                />
              </div>
            )}
            {!me && !pending && (
              <label className="field">
                <span>
                  Email{!staffRole && <span className="faint"> (optional)</span>}
                </span>
                <input type="email" autoComplete="email" value={email} placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void join()} />
                <span className="field-hint">
                  {staffRole ? 'Your studios and recordings are kept under this email. No password needed.' : 'So you’re recognised next time, on any device.'}
                </span>
              </label>
            )}
            <label className="field">
              Camera
              <select value={cam?.deviceId ?? ''} onChange={(e) => (cam ? void sm.switchDevice(cam.id, e.target.value) : void sm.addCamera(e.target.value))}>
                {!cam && <option value="">None</option>}
                {sm.cameras.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || 'Camera'}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Microphone
              <select value={mic?.deviceId ?? ''} onChange={(e) => (mic ? void sm.switchDevice(mic.id, e.target.value) : void sm.addMicrophone(e.target.value))}>
                {!mic && <option value="">None</option>}
                {sm.microphones.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || 'Microphone'}
                  </option>
                ))}
              </select>
              <Meter track={mic?.track} />
              {micRate != null && micRate < CALL_QUALITY_RATE && (
                <div className="alert warn small">
                  <Icon name="warn" size={14} />
                  <span>
                    This mic is running at call quality ({micRate / 1000} kHz), as Bluetooth headsets like AirPods do. Everyone will hear you fine, but
                    your recording will sound like a phone call. For full quality, choose your computer’s built-in mic or a wired one.
                  </span>
                </div>
              )}
            </label>
            <ExtraSources engine={engine} main={[cam, mic]} />
            <div className="field" role="group" aria-label="Audio setup">
              <span>What are you listening on?</span>
              <div className="choice-group">
                <button className={`choice ${sm.processing === 'voice' ? 'active' : ''}`} onClick={() => void sm.setProcessing('voice')} title="Echo cancellation and noise suppression on">
                  <Icon name="speaker" size={17} />
                  <span>
                    <strong>Speakers</strong>
                    <small>Echo cancellation on</small>
                  </span>
                </button>
                <button className={`choice ${sm.processing === 'studio' ? 'active' : ''}`} onClick={() => void sm.setProcessing('studio')} title="Raw 24-bit audio, no processing – wear headphones">
                  <Icon name="headphones" size={17} />
                  <span>
                    <strong>Headphones</strong>
                    <small>Raw studio audio</small>
                  </span>
                </button>
              </div>
            </div>
            <button className="primary big block" disabled={!canJoin} onClick={() => void join()}>
              {(!hw || joining) && <span className="spinner" />} Join studio
            </button>
            {joinError && (
              <div className="alert bad small">
                <Icon name="warn" size={14} /> {joinError}
              </div>
            )}
            {role === 'guest' && <span className="faint small" style={{ textAlign: 'center' }}>Nothing is recorded until the host starts a take.</span>}
          </div>
          <QualityCard engine={engine} />
        </div>
      </main>
    </div>
  );
}


/** The rate a microphone really runs at (see measureSampleRate), measured again whenever it changes. */
function useSampleRate(track?: MediaStreamTrack) {
  const [rate, setRate] = useState<number | null>(null);
  useEffect(() => {
    setRate(null);
    if (!track) return;
    let live = true;
    void measureSampleRate(track).then((r) => {
      if (!live) return;
      setRate(r);
      if (r != null && r < CALL_QUALITY_RATE) console.warn(`[takehouse] microphone at call quality: ${r} Hz (${track.label})`);
    });
    return () => {
      live = false;
    };
  }, [track]);
  return rate;
}

// ---------------------------------------------------------------------------

/** A guest who has asked to come in, waiting for a host or producer. */
function Lobby({ engine }: { engine: StudioEngine }) {
  const cam = engine.sources.primaryCamera;
  return (
    <div className="page">
      <AppBar>
        <span className="crumb-sep" />
        <span className="crumb ellipsis">{engine.studio?.name ?? ''}</span>
        <span className="grow" />
      </AppBar>
      <main className="center-page">
        <div className="card lobby-card">
          {cam && (
            <div className="tile self lobby-preview">
              <VideoEl track={cam.track} mirror />
            </div>
          )}
          {engine.fatal ? (
            <>
              <h2>{engine.fatal}</h2>
              <p className="muted">You can try again later with the same link, or ask the host.</p>
              <a className="btn" href="/" style={{ alignSelf: 'flex-start' }}>
                <Icon name="arrowLeft" size={16} /> Go to the home page
              </a>
            </>
          ) : (
            <>
              <div className="row" style={{ gap: 10 }}>
                <span className="spinner" />
                <h2>Waiting to be let in</h2>
              </div>
              <p className="muted">
                The host can see you’re here. You’ll join <strong>{engine.studio?.name ?? 'the studio'}</strong> as soon as they let you in;
                your camera and microphone are ready.
              </p>
              <button className="ghost" style={{ alignSelf: 'flex-start' }} onClick={() => void engine.leave().then(() => (location.href = '/'))}>
                <Icon name="leave" size={15} /> Leave
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function Room({ engine }: { engine: StudioEngine }) {
  useObservable(engine.mesh);
  const staff = engine.role === 'host' || engine.role === 'producer';
  const [tab, setTab] = useState<'sources' | 'team' | 'rooms' | 'uploads'>(staff ? 'team' : 'sources');
  const [sidebar, setSidebar] = useState(() => innerWidth > 900); // on phones the video needs the room
  useNow(500);
  useEffect(() => feedbackUi.enterRoom(), []); // its button moves to the top bar, clear of the call controls

  const waitingCount = staff ? engine.roster.filter((e) => e.roomId == null).length : 0;

  return (
    <div className="room">
      <div className="topbar">
        <div className="side">
          <Logo compact />
          <strong className="studio-name ellipsis">{engine.studio?.name}</strong>
          <span className="pill hide-narrow">{roleLabel(engine.role)}</span>
        </div>
        <RecStatus engine={engine} />
        <div className="side end">
          {!engine.connected && (
            <span className="pill warn" title="Your local recording continues while the connection is restored">
              <span className="spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} /> Reconnecting…
            </span>
          )}
          {waitingCount > 0 && (
            <button className="pill warn waiting-pill" onClick={() => (setSidebar(true), setTab('rooms'))} title="Someone is waiting in the lobby">
              <Icon name="users" size={13} /> {waitingCount} waiting
            </button>
          )}
          {engine.room && engine.room.id !== 'stage' && <span className="pill accent hide-narrow">{engine.room.name}</span>}
          <UploadPill engine={engine} />
          {staff && (
            <a className="btn sm hide-narrow" href={`/s/${engine.studioId}/files?k=${engine.key}`} target="_blank" rel="noreferrer">
              <Icon name="folder" size={15} /> Files
            </a>
          )}
          <button className="ghost sm" onClick={() => feedbackUi.show()} title="Send feedback">
            <Icon name="chat" size={15} /> <span className="hide-narrow">Feedback</span>
          </button>
          <AccountMenu inRoom />
          <button className="ghost icon" onClick={() => setSidebar(!sidebar)} title={sidebar ? 'Hide panel' : 'Show panel'} aria-label="Toggle panel">
            <Icon name="panel" />
          </button>
        </div>
      </div>

      <div className={`main ${sidebar ? '' : 'collapsed'}`}>
        <Stage engine={engine} />

        {sidebar && (
          <div className="sidebar">
            <div className="tabs" role="tablist">
              <button role="tab" aria-selected={tab === 'sources'} className={tab === 'sources' ? 'on' : ''} onClick={() => setTab('sources')}>
                My sources
              </button>
              {staff && (
                <button role="tab" aria-selected={tab === 'team'} className={tab === 'team' ? 'on' : ''} onClick={() => setTab('team')}>
                  Team {engine.peers.size > 0 && <span className="count">{engine.peers.size}</span>}
                </button>
              )}
              {staff && (
                <button role="tab" aria-selected={tab === 'rooms'} className={tab === 'rooms' ? 'on' : ''} onClick={() => setTab('rooms')}>
                  Rooms {waitingCount > 0 && <span className="count warn">{waitingCount}</span>}
                </button>
              )}
              <button role="tab" aria-selected={tab === 'uploads'} className={tab === 'uploads' ? 'on' : ''} onClick={() => setTab('uploads')}>
                Uploads
              </button>
            </div>
            <div className="body">
              {tab === 'sources' && <SourcesPanel engine={engine} />}
              {tab === 'team' && staff && <TeamPanel engine={engine} />}
              {tab === 'rooms' && staff && <RoomsPanel engine={engine} />}
              {tab === 'uploads' && <UploadsPanel studioId={engine.studioId} compact />}
            </div>
          </div>
        )}
      </div>

      <Controls engine={engine} />
      <Toasts engine={engine} />
    </div>
  );
}

/** 00:12:47, like a recorder's counter. */
const timecode = (ms: number) => {
  const t = Math.floor(ms / 1000);
  return [Math.floor(t / 3600), Math.floor(t / 60) % 60, t % 60].map((n) => String(n).padStart(2, '0')).join(':');
};

function RecStatus({ engine }: { engine: StudioEngine }) {
  const rec = engine.recording;
  if (!rec) {
    return (
      <div className="rec-status">
        <span className="dot" style={{ color: 'var(--faint)' }} /> Not recording
        {engine.lastTake && (
          <>
            <span className="sep" /> Take {engine.lastTake.takeNumber} done
          </>
        )}
      </div>
    );
  }
  const recStartLocal = engine.signaling ? engine.signaling.clock.toLocal(rec.startAt) : 0;
  const elapsed = Math.max(0, performance.timeOrigin + performance.now() - recStartLocal);
  const n = engine.recorders.size;
  return (
    <div className="rec-status live" title={n ? `Recording ${n} source${n > 1 ? 's' : ''} on this computer` : 'This take is recording'}>
      <span className="rec-word">
        <span className="dot pulse" /> REC
      </span>
      <span className="time">{timecode(elapsed)}</span>
      <span className="sep" />
      <span>Take {rec.takeNumber}</span>
      {n > 0 && (
        <>
          <span className="sep hide-narrow" />
          <span className="hide-narrow">
            {n} local track{n > 1 ? 's' : ''}
          </span>
        </>
      )}
    </div>
  );
}

function UploadPill({ engine }: { engine: StudioEngine }) {
  const jobs = engine.uploader.jobs.filter((j) => j.studioId === engine.studioId);
  if (!jobs.length) return null;
  const { total, uploaded } = engine.uploader.totals(jobs);
  const pending = jobs.filter((j) => !j.done).length;
  return (
    <span className={`pill ${pending ? '' : 'ok'}`} title="Your local recordings, uploaded to the server">
      <Icon name={pending ? 'upload' : 'check'} size={13} /> {pending ? `${total ? Math.floor((uploaded / total) * 100) : 0}% uploaded` : 'All uploaded'}
    </span>
  );
}

function Controls({ engine }: { engine: StudioEngine }) {
  const sm = engine.sources;
  const mic = sm.primaryMic;
  const cam = sm.primaryCamera;
  const staff = engine.role === 'host' || engine.role === 'producer';
  const rec = engine.recording;

  // M / Shift+M: mic send-mute / hard mute. V / Shift+V: the same for the camera.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.metaKey || e.ctrlKey || e.altKey || t.closest('input, select, textarea')) return;
      const key = e.key.toLowerCase();
      const src = key === 'm' ? sm.primaryMic : key === 'v' ? sm.primaryCamera : undefined;
      if (!src) return;
      e.preventDefault();
      if (e.shiftKey) engine.toggleHardMute(src.id);
      else engine.toggleSendMute(src.id);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [engine, sm]);

  return (
    <div className="controls">
      <div className="group">
        <MuteControl engine={engine} source={mic} what="Mic" keyName="M" onIcon="mic" offIcon="micOff" />
        <MuteControl engine={engine} source={cam} what="Camera" keyName="V" onIcon="cam" offIcon="camOff" />
        <Menu className="big" label={<><Icon name="screen" /> Screen</>}>
          {(close) => (
            <>
              <button onClick={() => { close(); void sm.addScreen({ live: true, record: true }); }}>
                Share live and record
                <span className="hint">Others see it; a full-quality copy is recorded locally</span>
              </button>
              <button onClick={() => { close(); void sm.addScreen({ live: false, record: true }); }}>
                Record only (private)
                <span className="hint">Saved for the edit, not shown on the call</span>
              </button>
              <button onClick={() => { close(); void sm.addScreen({ live: true, record: false }); }}>
                Share live only
                <span className="hint">Shown on the call, not recorded</span>
              </button>
              <ScreenSoundHint />
            </>
          )}
        </Menu>
        <Menu className="big" label={<><Icon name="plus" /> Add</>}>
          {(close) => (
            <>
              <span className="eyebrow menu-label">Extra sources, recorded locally</span>
              {sm.cameras.map((d) => (
                <button key={d.deviceId} onClick={() => { close(); void sm.addCamera(d.deviceId, { live: false, record: true }); }}>
                  {d.label || 'Camera'}
                  <span className="hint">Camera</span>
                </button>
              ))}
              {sm.microphones.map((d) => (
                <button key={d.deviceId} onClick={() => { close(); void sm.addMicrophone(d.deviceId, { live: false, record: true }); }}>
                  {d.label || 'Microphone'}
                  <span className="hint">Microphone</span>
                </button>
              ))}
            </>
          )}
        </Menu>
      </div>

      <div className="group center">
        {staff &&
          (rec ? (
            <>
              <button className="rec big" onClick={() => engine.stopRecording()}>
                <Icon name="stop" /> Stop recording
              </button>
              <button className="big" onClick={() => engine.addMarker()} title="Drop a marker on the take timeline">
                <Icon name="flag" /> Marker
              </button>
            </>
          ) : (
            <button className="rec big" onClick={() => engine.startRecording()} disabled={!engine.connected}>
              <Icon name="rec" /> Start recording
            </button>
          ))}
      </div>

      <div className="group end">
        <button className="big" onClick={() => void engine.runCheck('auto')} title="Replay the last 5 seconds of everything you're recording, read back from disk">
          <Icon name="replay" /> Check last 5 s
        </button>
        {staff && (
          <button className="big" onClick={() => engine.requestCheck()} title="Every participant replays their last 5 seconds and sends the clips to you">
            <Icon name="users" /> Check everyone
          </button>
        )}
        <button
          className="big danger ghost"
          onClick={async () => {
            if (engine.recording && !confirm('Recording is in progress. Leave and stop your local recording?')) return;
            await engine.leave();
            location.href = '/';
          }}
        >
          <Icon name="leave" /> Leave
        </button>
      </div>
    </div>
  );
}

/**
 * Main button = mute sending (amber: others don't get it, the recording carries on).
 * Side button = hard mute (red stripes: nothing sent, nothing recorded – silence/black keeps the file in sync).
 */
function MuteControl({ engine, source, what, keyName, onIcon, offIcon }: { engine: StudioEngine; source?: CapturedSource; what: string; keyName: string; onIcon: string; offIcon: string }) {
  const state = source ? muteState(source) : 'none';
  return (
    <div className="mute-group">
      <button
        className={`big ${state === 'send' ? 'send-muted' : state === 'hard' ? 'hard-muted' : ''}`}
        disabled={!source}
        onClick={() => source && engine.toggleSendMute(source.id)}
        title={
          state === 'none'
            ? `Stop sending your ${what.toLowerCase()} to the call – it keeps recording (${keyName})`
            : `Send your ${what.toLowerCase()} to the call again${state === 'hard' ? ' and resume recording' : ''} (${keyName})`
        }
      >
        <Icon name={state === 'none' ? onIcon : offIcon} />
        {state === 'none' ? `Mute ${what.toLowerCase()}` : state === 'send' ? 'Not sent · recording' : 'Not recorded'}
      </button>
      <button
        className={`big ${state === 'hard' ? 'hard-muted' : ''}`}
        disabled={!source}
        onClick={() => source && engine.toggleHardMute(source.id)}
        title={
          state === 'hard'
            ? `Resume recording your ${what.toLowerCase()} (stays off the call until you unmute) (Shift+${keyName})`
            : `Hard mute: don't send AND don't record your ${what.toLowerCase()} – the file gets silence/black (Shift+${keyName})`
        }
        aria-label={state === 'hard' ? `Resume recording ${what}` : `Hard mute ${what}`}
      >
        <Icon name="ban" size={15} /> {state === 'hard' ? 'Resume rec' : 'Hard'}
      </button>
    </div>
  );
}

function Toasts({ engine }: { engine: StudioEngine }) {
  const sm = engine.sources;
  return (
    <div className="toast-stack">
      <TakeNotice engine={engine} />
      {engine.warnings.map((w) => (
        <div className="toast" key={w} role="alert">
          <Icon name="warn" />
          <span className="grow">{w}</span>
          <button className="ghost sm icon" onClick={() => engine.dismissWarning(w)} aria-label="Dismiss">
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
      {sm.error && (
        <div className="toast" role="alert">
          <Icon name="warn" />
          <span className="grow">{sm.error}</span>
        </div>
      )}
      {sm.soundNote && (
        <div className="toast" role="status">
          <Icon name="speaker" />
          <span className="grow">{sm.soundNote}</span>
          <button className="ghost sm icon" onClick={() => sm.dismissSoundNote()} aria-label="Dismiss">
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/** After a take: where the files are going and, for the host, where to find them. */
function TakeNotice({ engine }: { engine: StudioEngine }) {
  const [dismissed, setDismissed] = useState<string | null>(null);
  const take = engine.lastTake;
  if (!take || engine.recording || dismissed === take.takeId) return null;
  const staff = engine.role === 'host' || engine.role === 'producer';
  const jobs = engine.uploader.jobs.filter((j) => j.takeId === take.takeId);
  const empty = jobs.filter((j) => j.done && j.size === 0).length;
  const pending = engine.uploader.pending.filter((j) => j.takeId === take.takeId).length;
  const save = engine.lastTakeSave;
  const folder = saveFolder().handle;
  return (
    <div className={`toast ${pending ? 'info' : 'ok'}`} role="status">
      <Icon name={take.localOnly ? 'folder' : pending ? 'upload' : 'check'} />
      <span className="grow">
        <strong>
          {take.localOnly
            ? `Take ${take.takeNumber} is on this computer.`
            : pending
              ? `Take ${take.takeNumber} stopped.`
              : `Take ${take.takeNumber} is safely on the server.`}
        </strong>{' '}
        {take.localOnly
          ? 'This studio keeps recordings on each person’s computer; the timeline has everyone’s timings.'
          : pending
            ? `Uploading ${pending} of your file${pending > 1 ? 's' : ''}; keep this tab open until it’s done.`
            : ''}
        {empty > 0 && ` ${empty} source${empty > 1 ? 's' : ''} recorded nothing.`}
        {save?.state === 'saving' && ' Copying to your folder…'}
        {save?.state === 'saved' && ` Copied to ${save.folder}.`}
        {save?.state === 'failed' && ` Couldn’t copy to your folder: ${save.error}.`}
        {staff && !take.localOnly && ' Everyone’s files, the timeline and downloads are on the Files page.'}
      </span>
      {save?.state === 'needs-permission' && folder && (
        <button className="sm primary" onClick={() => void engine.saveLastTake(true)} title="The browser asks again before writing to the folder">
          <Icon name="folder" size={14} /> Save to {folder.name}
        </button>
      )}
      {take.localOnly ? (
        <a className="btn sm" href="/local" target="_blank" rel="noreferrer">
          On this computer
        </a>
      ) : (
        staff && (
          <a className="btn sm" href={`/s/${engine.studioId}/files?k=${engine.key}`} target="_blank" rel="noreferrer">
            Open files
          </a>
        )
      )}
      <button className="ghost sm icon" onClick={() => setDismissed(take.takeId)} aria-label="Dismiss">
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

export { describeTrack, initials };

/** What sound a screen share can carry, so nobody is surprised by a silent game or video. */
function ScreenSoundHint() {
  return (
    <span className="menu-note">
      <Icon name="speaker" size={13} /> For the computer’s sound (apps, games, videos), choose <strong>Entire Screen</strong> and switch on
      <strong> “Also share system audio”</strong>. It’s recorded as its own track. A tab can share its own sound; single windows share
      none. Safari and Firefox share no sound: there, add a virtual audio device such as BlackHole as a microphone.
    </span>
  );
}

/**
 * Extra cameras, microphones and screens, set up before joining so the 6-second test covers them too.
 * Extras are recorded at the quality the Recording quality card shows; the call shows the main camera and mic (and a
 * screen if shared live).
 */
function ExtraSources({ engine, main }: { engine: StudioEngine; main: (CapturedSource | undefined)[] }) {
  const sm = engine.sources;
  const extras = sm.active.filter((s) => !main.includes(s));
  const usedCams = new Set(sm.active.filter((s) => s.kind === 'camera').map((s) => s.deviceId));
  const usedMics = new Set(sm.active.filter((s) => s.kind === 'microphone').map((s) => s.deviceId));
  const freeCams = sm.cameras.filter((d) => !usedCams.has(d.deviceId));
  const freeMics = sm.microphones.filter((d) => !usedMics.has(d.deviceId));
  return (
    <div className="field">
      <span>
        More sources <span className="faint">(optional)</span>
      </span>
      {extras.map((s) => (
        <div key={s.id} className="extra-source">
          <Icon name={s.kind === 'camera' ? 'cam' : s.kind === 'screen' ? 'screen' : s.kind === 'microphone' ? 'mic' : 'speaker'} size={15} />
          <span className="grow ellipsis">{s.label}</span>
          {(s.kind === 'microphone' || s.kind === 'system-audio') && <Meter track={s.track} />}
          <span className="faint small">{s.use.live ? 'Live + recorded' : 'Recorded'}</span>
          <button className="ghost sm icon" title="Remove" aria-label={`Remove ${s.label}`} onClick={() => sm.remove(s.id)}>
            <Icon name="close" size={13} />
          </button>
        </div>
      ))}
      <Menu down label={<><Icon name="plus" size={15} /> Add a camera, microphone or screen</>}>
        {(close) => (
          <>
            <span className="eyebrow menu-label">Recorded as the quality card shows, and covered by the test</span>
            {freeCams.map((d) => (
              <button key={d.deviceId} onClick={() => { close(); void sm.addCamera(d.deviceId, { live: false, record: true }); }}>
                {d.label || 'Camera'}
                <span className="hint">Another camera</span>
              </button>
            ))}
            {freeMics.map((d) => (
              <button key={d.deviceId} onClick={() => { close(); void sm.addMicrophone(d.deviceId, { live: false, record: true }); }}>
                {d.label || 'Microphone'}
                <span className="hint">Another microphone or audio device</span>
              </button>
            ))}
            <button onClick={() => { close(); void sm.addScreen({ live: false, record: true }); }}>
              Screen, window or tab
              <span className="hint">Recorded privately; you can show it on the call once you’re in</span>
            </button>
            <ScreenSoundHint />
          </>
        )}
      </Menu>
    </div>
  );
}
